import { computed, ref } from 'vue'
import { useApolloClient, useMutation } from '@vue/apollo-composable'
import {
  LATEST_UPDATES_QUERY,
  MARK_CASE_FOLLOWUPS_READ_MUTATION,
} from '~/graphql/cases/queries'

/**
 * Single source of truth for the Latest Updates dashboard:
 *   - `items`: per-case unread cards (most-recent unread follow-up
 *     of each case the user hasn't read yet).
 *   - `total`: distinct unread count across all those cases.
 *   - `byCompany`: per-company breakdown (used by the header
 *     notification bubble — both views consume this same composable
 *     so they're always in lockstep, with one polling loop instead
 *     of two).
 *
 * Module-level singleton, same pattern as `useCaseNavigation`. The
 * polling timer + visibility hook + Apollo client are kept at module
 * scope so `invalidateLatestUpdates()` can fire from anywhere (e.g.
 * `useFollowUps.markFollowUpRead` after the server commit) without
 * being inside a setup() context.
 */

const _items = ref([])
const _total = ref(0)
const _byCompany = ref([])
const _loading = ref(false)
const _hasInitialized = ref(false)

// Two polling "modes":
//   - LITE_LIMIT (5): used while the dashboard is NOT mounted. The
//     server fetches the 5 most recent unread items plus stats — small
//     enough to leave running across the whole authed session and big
//     enough to feed the header popover AND the notifications watcher
//     (which diffs `items` by `followUpId` to detect arrivals).
//   - FULL_LIMIT (100): used while the dashboard is mounted. Brings
//     in the full per-case items array for the cards.
//
// The mode is driven by `_dashboardConsumers`: each call to
// `activateDashboardMode` bumps the counter (and triggers an
// immediate full fetch). `deactivateDashboardMode` decrements; when
// it hits zero, future polls revert to lite. This way nothing needs
// to coordinate two timers — there's only ever one polling tick.
const LITE_LIMIT = 5
const FULL_LIMIT = 100

// Foreground cadence (tab visible). In the background we keep polling
// but slow down — browsers throttle background timers to ~1/min anyway,
// so this keeps notifications flowing without a busy poll.
// NOTE (later): notifying with the tab/browser fully closed needs a
// Service Worker + Web Push; this background polling only covers an
// open-but-hidden tab.
const POLL_INTERVAL_MS = 30_000
const POLL_INTERVAL_HIDDEN_MS = 60_000

let _pollTimer = null
let _isPolling = false
let _visibilityHandler = null
let _resolveClient = null
let _dashboardConsumers = 0

function currentLimit() {
  return _dashboardConsumers > 0 ? FULL_LIMIT : LITE_LIMIT
}

async function _coreFetch(overrideLimit) {
  if (!_resolveClient) return
  const limit = overrideLimit != null ? overrideLimit : currentLimit()

  _loading.value = true
  try {
    const { data } = await _resolveClient().query({
      query: LATEST_UPDATES_QUERY,
      variables: { limit },
      fetchPolicy: 'network-only',
    })

    const payload = data?.getLatestUpdates ?? { items: [], total: 0, byCompany: [] }

    // Always overwrite items — the notifications watcher relies on
    // this assignment firing every tick so it can diff arrivals by
    // `followUpId`. Skipping the update (e.g. "preserve dashboard
    // items in lite mode") silently breaks the alerts.
    _items.value = Array.isArray(payload.items) ? payload.items : []
    _total.value = typeof payload.total === 'number' ? payload.total : 0
    _byCompany.value = Array.isArray(payload.byCompany) ? payload.byCompany : []
  } catch (err) {
    console.error('[useLatestUpdates] fetch failed', err)
  } finally {
    _loading.value = false
  }
}

function _scheduleNext() {
  if (_pollTimer) {
    clearTimeout(_pollTimer)
    _pollTimer = null
  }
  if (!_isPolling) return

  // Keep polling even when the tab is hidden so background follow-ups
  // still fire notifications — just at the slower hidden cadence.
  const hidden = typeof document !== 'undefined' && document.hidden
  const interval = hidden ? POLL_INTERVAL_HIDDEN_MS : POLL_INTERVAL_MS

  _pollTimer = setTimeout(async () => {
    await _coreFetch()
    _scheduleNext()
  }, interval)
}

export function startLatestUpdatesPolling() {
  if (_isPolling) return
  _isPolling = true
  if (typeof document !== 'undefined') {
    _visibilityHandler = () => {
      // Returning to the tab: fetch right away so the view is fresh.
      if (!document.hidden) _coreFetch().catch(() => {})

      // Re-arm so the cadence switches between foreground (30s) and
      // background (60s) immediately on visibility change.
      _scheduleNext()
    }
    document.addEventListener('visibilitychange', _visibilityHandler)
  }
  _scheduleNext()
}

export function stopLatestUpdatesPolling() {
  _isPolling = false
  if (_pollTimer) {
    clearTimeout(_pollTimer)
    _pollTimer = null
  }
  if (_visibilityHandler && typeof document !== 'undefined') {
    document.removeEventListener('visibilitychange', _visibilityHandler)
    _visibilityHandler = null
  }
}

/**
 * Full module-level reset — used by `authStore.logout` to make sure
 * nothing from the previous session leaks into the next one. Stops
 * the polling timer, clears the in-memory snapshot, and resets the
 * dashboard-mode counter so a fresh login starts in lite mode.
 */
export function resetLatestUpdatesState() {
  stopLatestUpdatesPolling()
  _items.value = []
  _total.value = 0
  _byCompany.value = []
  _hasInitialized.value = false
  _dashboardConsumers = 0
}

/**
 * Force an immediate refetch + reset the polling window. Safe to
 * call from Pinia actions or any post-mutation handler — has no
 * dependency on a setup() context.
 */
export async function invalidateLatestUpdates(_reason) {
  await _coreFetch()
  if (_isPolling) _scheduleNext()
}

/**
 * Register a consumer that needs the full items array (= the
 * dashboard). Polling bumps from lite (`limit: 0`) to full
 * (`limit: 100`) while the counter is > 0. Pair with
 * `deactivateDashboardMode` on the consumer's unmount.
 *
 * The first activation triggers an immediate full fetch so the
 * dashboard renders against fresh items right away instead of
 * waiting up to 30s for the next poll tick.
 */
export async function activateDashboardMode() {
  _dashboardConsumers += 1
  if (_dashboardConsumers === 1) {
    await _coreFetch(FULL_LIMIT)
  }
}

export function deactivateDashboardMode() {
  _dashboardConsumers = Math.max(0, _dashboardConsumers - 1)
}

export function useLatestUpdates() {
  const { resolveClient } = useApolloClient()

  // Cache the apollo client at module scope on first invocation so
  // background functions (polling, invalidation) can run without a
  // setup context.
  if (!_resolveClient) _resolveClient = resolveClient

  /**
   * Page-mount entry point. Forces a fresh fetch — module state may
   * still hold the previous user's data after an account switch
   * without a hard reload.
   */
  async function init() {
    _hasInitialized.value = false
    await _coreFetch()
    _hasInitialized.value = true
  }

  /**
   * Bulk-mark every unread follow-up of a case as read for the
   * caller. Optimistically removes the card and refreshes the total
   * + byCompany breakdown so the header badge updates without a
   * full polling tick.
   */
  async function markCaseRead(caseId) {
    if (caseId == null) return false
    const targetId = String(caseId)

    // Optimistic: drop the card immediately so the UI feels instant.
    const beforeItems = _items.value
    const removed = beforeItems.find(c => String(c.caseId) === targetId)

    if (!removed) return false

    _items.value = beforeItems.filter(c => String(c.caseId) !== targetId)
    _total.value = Math.max(0, _total.value - (removed.totalUnreadFollowUps ?? 1))
    if (removed.companyId) {
      const ix = _byCompany.value.findIndex(c => String(c.id) === String(removed.companyId))
      if (ix !== -1) {
        const next = [..._byCompany.value]

        const decremented = Math.max(
          0,
          (next[ix].count ?? 0) - (removed.totalUnreadFollowUps ?? 1),
        )

        if (decremented === 0) {
          next.splice(ix, 1)
        } else {
          next[ix] = { ...next[ix], count: decremented }
        }
        _byCompany.value = next
      }
    }

    try {
      const { mutate } = useMutation(MARK_CASE_FOLLOWUPS_READ_MUTATION)

      await mutate({ caseId: targetId })

      // Reconcile against the server so the optimistic numbers stay
      // honest (e.g. if another tab marked some of them too).
      invalidateLatestUpdates('mark-case-read').catch(() => {})

      return true
    } catch (err) {
      console.error('[useLatestUpdates] markCaseRead failed', err)

      // Roll back the optimistic update.
      _items.value = beforeItems

      return false
    }
  }

  const ready = computed(() => _hasInitialized.value)
  const hasItems = computed(() => _items.value.length > 0)

  return {
    items: _items,
    total: _total,
    byCompany: _byCompany,
    loading: _loading,
    ready,
    hasItems,

    init,
    fetchNow: _coreFetch,
    markCaseRead,

    start: startLatestUpdatesPolling,
    stop: stopLatestUpdatesPolling,
    invalidate: invalidateLatestUpdates,
    activateDashboardMode,
    deactivateDashboardMode,
  }
}
